import { useEffect, useState } from 'react'
import { Calendar, Fuel, Car, User, Hash, Clock, Gauge, Droplet, Coins, MessageSquare, FileText, Wallet } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'
import { Linha, CampoCalc, larguraNumero } from './LancamentoDepositos'

interface Contexto {
  veiculo: {
    veicod: string; veipla: string; combustivel: string; hodometro: number; media: number
    tanque: number; digitos: number; aditivado: boolean; dataBaixa: string | null
  } | null
  letraPadrao: string
  posto: { pracod: string; pranom: string; cnpj: string } | null
  valorUnitario: number
  descontoPct: number
  toleranciaValor: number
  toleranciaKms: number
  hodAnterior: number | null
  hodPosterior: number | null
  pagamentoPadrao: string
  tipoDespesa: { codigo: number; descricao: string }
}

const hoje = () => new Date().toISOString().slice(0, 10)
const fmtDataBr = (iso: string) => iso.split('-').reverse().join('/')

// Abastecimento em posto externo: cabeçalho (GeneXus TDPS056) + despesa (TDPS053) numa tela só.
// Com arla: Lançamento ARLA Externo (TDPS063 → PDPS073), combustível fixo ARLA32 e no máximo 200 L.
const LIMITE_LITROS_ARLA = 200

export default function LancamentosExternos({ arla = false }: { arla?: boolean }) {
  const { empresaCod, empresaAtual } = useEmpresa()
  const [combustiveis, setCombustiveis] = useState<{ letra: string; nome: string }[]>([])

  const [data, setData] = useState(hoje())
  const [veicod, setVeicod] = useState('')
  const [motcod, setMotcod] = useState('')
  const [motnome, setMotnome] = useState('')
  const [pracod, setPracod] = useState('')
  const [pranom, setPranom] = useState('')
  const [letra, setLetra] = useState('')
  const [pagamento, setPagamento] = useState('')
  const [notaFiscal, setNotaFiscal] = useState('')
  const [horario, setHorario] = useState('12:00')
  const [hodometro, setHodometro] = useState('')
  const [litros, setLitros] = useState('')
  const [valor, setValor] = useState('')
  const [obscod, setObscod] = useState('1')
  const [obsdes, setObsdes] = useState('')

  const [ctx, setCtx] = useState<Contexto | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [msg, setMsg] = useState<{ texto: string; ok: boolean } | null>(null)

  useEffect(() => {
    api.get('/combustivel/combustiveis-posto').then(r => setCombustiveis(r.data))
    api.get('/combustivel/observacoes').then(r => {
      const o = r.data.find((x: any) => String(x.obscod) === '1')
      if (o) setObsdes(o.obsdes)
    })
  }, [])

  // Troca de veículo volta o combustível pro padrão do veículo (PVERCOM3).
  useEffect(() => { setLetra('') }, [veicod])

  useEffect(() => {
    if (!veicod || !data) { setCtx(null); return }
    api.get('/combustivel/externo-contexto', {
      params: { veicod, data, pracod, letra, empresa: empresaCod ?? 1, arla: arla ? '1' : undefined },
    }).then(r => {
      setCtx(r.data)
      if (!letra) setLetra(r.data.letraPadrao)
      setPagamento(p => p || r.data.pagamentoPadrao)
    })
  }, [veicod, data, pracod, letra, empresaCod, arla])

  const vei = ctx?.veiculo
  const vluni = Number(ctx?.valorUnitario) || 0
  const litrosNum = Number(litros) || 0
  const hodNum = Number(hodometro) || 0
  const dataFutura = !!data && data > hoje()
  const veiculoBaixado = !!vei?.dataBaixa && data > vei.dataBaixa
  const excedeTanque = arla ? litrosNum > LIMITE_LITROS_ARLA : !!vei && vei.tanque > 0 && litrosNum > vei.tanque
  const media = ctx?.hodAnterior != null && litrosNum > 0 && hodNum > ctx.hodAnterior
    ? (hodNum - ctx.hodAnterior) / litrosNum : 0

  function alterarLitros(v: string) {
    setLitros(v)
    const l = Number(v) || 0
    if (vluni > 0) setValor(l > 0 ? (l * vluni).toFixed(2) : '')
  }

  function limparFormulario() {
    setData(hoje())
    setVeicod(''); setCtx(null)
    setMotcod(''); setMotnome('')
    setPracod(''); setPranom('')
    setLetra(''); setPagamento('')
    setNotaFiscal(''); setHorario('12:00')
    setHodometro(''); setLitros(''); setValor('')
  }

  async function salvar() {
    setMsg(null)
    if (!veicod || !motcod || !pracod || (!letra && !arla) || !litros || !valor) {
      setMsg({ texto: 'Preencha veículo, motorista, posto, combustível, litros e valor.', ok: false })
      return
    }
    setSalvando(true)
    try {
      const r = await api.post('/combustivel/lancamentos-externos', {
        data, veicod, motcod, pracod, letra, pagamento, notaFiscal, horario,
        hodometro: hodNum, litros: litrosNum, valor: Number(valor),
        observacao: obscod || null, empresa: empresaCod ?? 1, arla,
      })
      setMsg({ texto: `Lançamento ${r.data.dpsnum} salvo — valor R$ ${Number(r.data.valorTotal).toFixed(2)}.`, ok: true })
      limparFormulario()
    } catch (e: any) {
      setMsg({ texto: e?.response?.data?.error || 'Erro ao salvar lançamento.', ok: false })
    } finally {
      setSalvando(false)
    }
  }

  const alerta = (texto: string) => (
    <div style={{
      marginTop: '0.5rem', fontSize: '0.78rem', fontWeight: 700, color: '#991b1b',
      background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8, padding: '0.5rem 0.75rem',
    }}>
      ⚠ {texto}
    </div>
  )

  const secao = (titulo: string) => (
    <div style={{
      marginTop: '1.1rem', paddingBottom: '0.3rem', fontSize: '0.7rem', fontWeight: 800,
      color: '#1a6ea8', letterSpacing: '0.05em', borderBottom: '2px solid #dbeafe',
    }}>
      {titulo}
    </div>
  )

  return (
    <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
      <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
          <span style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)',
          }}>
            <Fuel size={18} color="#fff" />
          </span>
          <div>
            <h2>{arla ? 'Lançamento ARLA Externo' : 'Lançamentos Externos'}</h2>
            <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>{arla ? 'Consumo de ARLA32 em posto de fora' : 'Abastecimento em posto de fora'}</div>
          </div>
        </div>
      </div>

      <div className="modal-corpo">
        <Linha label="Num. Registro" icon={Hash}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
            <CampoCalc rotulo="" valor="gerado ao salvar" largura={larguraNumero(12)} />
            {empresaAtual && (
              <span style={{
                fontSize: '0.7rem', fontWeight: 700, color: '#1a6ea8', background: '#eff6ff',
                border: '1px solid #bfdbfe', borderRadius: 999, padding: '0.2rem 0.7rem',
              }}>
                {empresaCod} — {empresaAtual.empfan?.trim() || empresaAtual.empnom?.trim()}
              </span>
            )}
          </div>
        </Linha>

        <Linha label="Data" icon={Calendar}>
          <div style={{ display: 'flex', gap: '0.6rem' }}>
            <input type="date" value={data} max={hoje()} onChange={e => setData(e.target.value)} style={{ maxWidth: 170 }} />
            <input type="time" value={horario} onChange={e => setHorario(e.target.value)} style={{ maxWidth: 120 }} />
          </div>
          {dataFutura && alerta('Data inválida — não pode ser maior que hoje.')}
        </Linha>

        <Linha label="Veículo" icon={Car}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.8rem', flexWrap: 'wrap' }}>
            <div style={{ maxWidth: 140 }}>
              <LookupSelect
                endpoint="/erp/veiculos"
                codKey="veicod"
                descKey="veipla"
                tagKey="veicod"
                codValue={veicod}
                descValue={veicod}
                onSelect={cod => setVeicod(cod)}
                placeholder="Buscar veículo..."
              />
            </div>
            {vei && (
              <>
                <CampoCalc rotulo="PLACA" valor={vei.veipla || '—'} largura={larguraNumero(8)} />
                <CampoCalc rotulo="COMBUSTÍVEL" valor={vei.combustivel || '—'} largura={larguraNumero(7)} />
                <CampoCalc rotulo="HODÔMETRO" valor={String(vei.hodometro)} largura={larguraNumero(7)} />
              </>
            )}
          </div>
          {vei?.dataBaixa && (
            veiculoBaixado
              ? alerta(`Lançamento para veículo baixado! (baixa em ${fmtDataBr(vei.dataBaixa)})`)
              : <div style={{ fontSize: '0.72rem', marginTop: 4, color: '#94a3b8' }}>Baixa em {fmtDataBr(vei.dataBaixa)}</div>
          )}
        </Linha>

        <Linha label="Motorista" icon={User}>
          <LookupSelect
            endpoint="/combustivel/motoristas"
            codKey="motcod"
            descKey="motnome"
            tagKey="motcod"
            codValue={motcod}
            descValue={motnome}
            onSelect={(cod, desc) => { setMotcod(cod); setMotnome(desc) }}
            placeholder="Buscar motorista..."
            listWidth={320}
          />
        </Linha>

        <Linha label="Posto" icon={Fuel}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
            <LookupSelect
              endpoint="/combustivel/postos-externos"
              codKey="pracod"
              descKey="pranom"
              tagKey="pracod"
              codValue={pracod}
              descValue={pranom}
              onSelect={(cod, desc) => { setPracod(cod); setPranom(desc) }}
              placeholder="Buscar posto..."
              listWidth={320}
            />
            {ctx?.posto?.cnpj && <span style={{ fontSize: '0.78rem', color: '#57606f', whiteSpace: 'nowrap' }}>CNPJ {ctx.posto.cnpj}</span>}
          </div>
        </Linha>

        {secao('DESPESA')}

        <Linha label="Combustível" icon={Droplet}>
          {arla ? (
            <span style={{
              fontSize: '0.75rem', fontWeight: 700, color: '#1a6ea8', background: '#eff6ff',
              border: '1px solid #bfdbfe', borderRadius: 999, padding: '0.25rem 0.8rem',
            }}>
              ARLA32
            </span>
          ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <select value={letra} onChange={e => setLetra(e.target.value)} style={{ maxWidth: 170 }}>
              <option value="">Selecione...</option>
              {combustiveis.map(c => <option key={c.letra} value={c.letra}>{c.nome}</option>)}
            </select>
            {letra && (
              <span style={{
                fontSize: '0.7rem', fontWeight: 700, color: '#1a6ea8', background: '#eff6ff',
                border: '1px solid #bfdbfe', borderRadius: 999, padding: '0.2rem 0.7rem',
              }}>
                {letra}{vei?.aditivado ? ' · aditivado' : ''}
              </span>
            )}
          </div>
          )}
        </Linha>

        <Linha label="Pagamento" icon={Wallet}>
          <select value={pagamento} onChange={e => setPagamento(e.target.value)} style={{ maxWidth: 150 }}>
            <option value="A">À vista</option>
            <option value="P">À prazo</option>
          </select>
        </Linha>

        <Linha label="Nota Fiscal" icon={FileText}>
          <input type="text" maxLength={9} value={notaFiscal} onChange={e => setNotaFiscal(e.target.value)} style={{ width: 9 * 9 + 40 }} />
        </Linha>

        <Linha label="Hodômetro" icon={Gauge}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
            <input type="number" value={hodometro} onChange={e => setHodometro(e.target.value)} style={{ width: larguraNumero(7) }} />
            <CampoCalc rotulo="ANTERIOR" valor={ctx?.hodAnterior != null ? String(ctx.hodAnterior) : '—'} largura={larguraNumero(7)} />
            <CampoCalc rotulo="POSTERIOR" valor={ctx?.hodPosterior != null ? String(ctx.hodPosterior) : '—'} largura={larguraNumero(7)} />
          </div>
        </Linha>

        <Linha label="Litros" icon={Droplet}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
            <div>
              <input
                type="number" step="0.001" value={litros} onChange={e => alterarLitros(e.target.value)}
                style={{ width: larguraNumero(9), borderColor: excedeTanque ? '#dc2626' : undefined }}
              />
              {arla ? (
                <div style={{ fontSize: '0.72rem', marginTop: 4, color: excedeTanque ? '#dc2626' : '#94a3b8', fontWeight: excedeTanque ? 700 : 400 }}>
                  {excedeTanque ? `Acima do máximo de ${LIMITE_LITROS_ARLA}L de ARLA` : `Máximo: ${LIMITE_LITROS_ARLA}L`}
                </div>
              ) : vei && vei.tanque > 0 && (
                <div style={{ fontSize: '0.72rem', marginTop: 4, color: excedeTanque ? '#dc2626' : '#94a3b8', fontWeight: excedeTanque ? 700 : 400 }}>
                  {excedeTanque ? `Acima da capacidade do tanque (${vei.tanque}L)` : `Capacidade do tanque: ${vei.tanque}L`}
                </div>
              )}
            </div>
            {!arla && <CampoCalc rotulo="MÉDIA KM/L" valor={media ? media.toFixed(2) : '—'} largura={larguraNumero(6)} />}
          </div>
        </Linha>

        <Linha label="Valor" icon={Coins}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <span style={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 700, letterSpacing: '0.03em' }}>VLR DESPESA</span>
              <input type="number" step="0.01" value={valor} onChange={e => setValor(e.target.value)} style={{ width: larguraNumero(10) }} />
            </div>
            <CampoCalc rotulo="V. UNITÁRIO NO POSTO" valor={vluni ? vluni.toFixed(4) : '—'} largura={larguraNumero(8)} />
          </div>
          {ctx?.posto && vluni <= 0 && alerta(`Posto sem preço cadastrado para ${arla ? 'ARLA32' : 'esse combustível'} na data.`)}
        </Linha>

        {/* ARLA: observação oculta, grava o padrão 1 do PDPS073. */}
        {!arla && (
          <Linha label="Observação" icon={MessageSquare}>
            <LookupSelect
              endpoint="/combustivel/observacoes"
              codKey="obscod"
              descKey="obsdes"
              tagKey="obscod"
              codValue={obscod}
              descValue={obsdes}
              onSelect={(cod, desc) => { setObscod(cod); setObsdes(desc) }}
              placeholder="Buscar observação..."
            />
          </Linha>
        )}

        {msg && (
          <div style={{
            marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8,
            background: msg.ok ? '#d1fae5' : '#fee2e2',
            color: msg.ok ? '#065f46' : '#991b1b',
            border: `1px solid ${msg.ok ? '#a7f3d0' : '#fecaca'}`,
          }}>
            {msg.texto}
          </div>
        )}
      </div>

      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={limparFormulario} disabled={salvando}>Limpar</button>
        <button className="btn btn-primary" onClick={salvar} disabled={salvando || dataFutura || veiculoBaixado}>
          {salvando ? 'Salvando...' : 'Salvar lançamento'}
        </button>
      </div>
    </div>
  )
}
